import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { classesAPI, schedulesAPI, bookingsAPI, paymentsAPI, termsAPI, waitlistAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { Clock, Users, MapPin, Calendar, ChevronLeft, Sparkles, Bell, AlertCircle, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import AddToCalendarButton from '../components/ui/AddToCalendarButton';
import { formatPrice, formatTime, DAYS_OF_WEEK, getCategoryInfo } from '../lib/utils';

const ClassDetail = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [classData, setClassData] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [bookingType, setBookingType] = useState('term_enrollment');
  const [termLength, setTermLength] = useState('6weeks');
  const [pricingType, setPricingType] = useState('term'); // 'term', 'one_off', 'monthly'
  const [studentName, setStudentName] = useState('');
  const [studentAge, setStudentAge] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Waitlist state
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);
  const [userWaitlistEntries, setUserWaitlistEntries] = useState([]);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [waitlistStudentName, setWaitlistStudentName] = useState('');
  
  // Card saving state
  const [savedCard, setSavedCard] = useState(null);
  const [savingCard, setSavingCard] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);

  useEffect(() => {
    fetchClassData();
  }, [classId]);

  useEffect(() => {
    if (isAuthenticated && schedules.length > 0) {
      fetchUserWaitlist();
    }
  }, [isAuthenticated, schedules]);

  const fetchClassData = async () => {
    try {
      const [classRes, schedulesRes] = await Promise.all([
        classesAPI.getOne(classId),
        schedulesAPI.getAll(null, classId),
      ]);
      setClassData(classRes.data);
      setSchedules(schedulesRes.data);
    } catch (error) {
      toast.error('Failed to load class details');
      navigate('/classes');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserWaitlist = async () => {
    if (!isAuthenticated) return;
    try {
      // Get waitlist entries for each schedule
      const entries = [];
      for (const schedule of schedules) {
        const res = await waitlistAPI.getAll(schedule.id);
        const userEntry = res.data?.find(e => e.user_id === user?.id);
        if (userEntry) {
          entries.push({ ...userEntry, schedule_id: schedule.id });
        }
      }
      setUserWaitlistEntries(entries);
    } catch (error) {
      console.error('Failed to fetch waitlist:', error);
    }
  };

  const fetchSavedCard = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await paymentsAPI.getMySavedCard();
      setSavedCard(res.data);
    } catch (error) {
      console.error('Failed to fetch saved card:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSavedCard();
    }
  }, [isAuthenticated]);

  const isScheduleFull = (schedule) => {
    // Check if schedule has capacity info and is full
    return schedule.capacity && schedule.current_enrolled >= schedule.capacity;
  };

  const getUserWaitlistForSchedule = (scheduleId) => {
    return userWaitlistEntries.find(e => e.schedule_id === scheduleId);
  };

  const handleJoinWaitlist = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!selectedSchedule || !waitlistStudentName) {
      toast.error('Please select a schedule and enter student name');
      return;
    }

    setJoiningWaitlist(true);
    try {
      await waitlistAPI.join({
        schedule_id: selectedSchedule,
        student_name: waitlistStudentName,
      });
      toast.success('Successfully joined the waitlist! We\'ll notify you when a spot opens.');
      setWaitlistModalOpen(false);
      setWaitlistStudentName('');
      setSelectedSchedule(null);
      fetchUserWaitlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const handleLeaveWaitlist = async (waitlistId) => {
    try {
      await waitlistAPI.leave(waitlistId);
      toast.success('Removed from waitlist');
      fetchUserWaitlist();
    } catch (error) {
      toast.error('Failed to leave waitlist');
    }
  };

  const handleBooking = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!studentName || !studentAge) {
      toast.error('Please fill in all required fields');
      return;
    }

    // For classes with schedules, require schedule selection
    if (schedules.length > 0 && !selectedSchedule) {
      toast.error('Please select a schedule');
      return;
    }

    // Check if user has a saved card
    if (!savedCard?.has_card) {
      toast.error('Please add a payment card first');
      setShowAddCard(true);
      return;
    }

    setSubmitting(true);
    try {
      // Calculate price based on selected pricing type
      let price = 0;
      let bookingTermLength = termLength;
      
      if (pricingType === 'one_off') {
        price = classData.one_off_price;
        bookingTermLength = 'single';
      } else if (pricingType === 'monthly') {
        price = classData.subscription_price;
        bookingTermLength = 'monthly';
      } else {
        // Term pricing - use six_week_price for 6 weeks, term_price for 12 weeks
        price = termLength === '12weeks' ? classData.term_price : classData.six_week_price;
      }

      const bookingRes = await bookingsAPI.create({
        class_id: classId,
        schedule_id: selectedSchedule || null,
        student_name: studentName,
        student_age: parseInt(studentAge),
        term_length: bookingTermLength,
        total_amount: price,
        pricing_type: pricingType,
        payment_status: 'awaiting_authorization', // New: Don't charge immediately
      });

      // Check if added to waitlist instead of booked
      if (bookingRes.data.waitlisted) {
        toast.success(bookingRes.data.message, { duration: 8000 });
        setBookingOpen(false);
        fetchUserWaitlist();
        setSubmitting(false);
        return;
      }

      // Show bundle discount message if applied
      if (bookingRes.data.bundle_info) {
        toast.success(bookingRes.data.message, { duration: 6000 });
      }

      // Success! Booking created with card on file, awaiting admin authorization
      toast.success(
        `Booking confirmed for ${studentName}! Your card ending in ${savedCard.last4} is saved. Payment will be taken when your booking is authorized.`,
        { duration: 8000 }
      );
      setBookingOpen(false);
      navigate('/dashboard'); // Redirect to parent dashboard
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
      setSubmitting(false);
    }
  };

  const categoryInfo = classData ? getCategoryInfo(classData.category) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-8 w-48 bg-neutral-200 rounded" />
            <div className="h-96 bg-neutral-200 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!classData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-pink-50">
      <Navbar />
      <Toaster position="top-right" richColors />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/classes')}
          className="mb-6"
          data-testid="back-to-classes"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Classes
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image */}
            <div className="relative rounded-2xl overflow-hidden aspect-video bg-neutral-100">
              {loading ? (
                <div className="w-full h-full bg-gradient-to-br from-pink-100 to-purple-100 animate-pulse" />
              ) : classData.image_url ? (
                <img 
                  src={classData.image_url}
                  alt={classData.name}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center">
                  <Sparkles className="w-16 h-16 text-pink-300" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              {categoryInfo && (
                <Badge className={`absolute top-4 left-4 ${categoryInfo.color}`}>
                  {categoryInfo.label}
                </Badge>
              )}
            </div>

            {/* Class Info */}
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">
                {classData.name}
              </h1>
              <p className="text-lg text-neutral-600 leading-relaxed">
                {classData.description}
              </p>
            </div>

            {/* Details Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-pink-50 to-white border-pink-100">
                <CardContent className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Age Range <span className="text-xs text-neutral-400">(age is a guide)</span></p>
                    <p className="font-semibold">{classData.age_min} - {classData.age_max} years</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
                <CardContent className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Duration</p>
                    <p className="font-semibold">{classData.duration_minutes} minutes</p>
                  </div>
                </CardContent>
              </Card>

              {/* Show class-specific schedule if available */}
              {(classData.day_of_week || classData.start_time) && (
                <Card className="bg-gradient-to-br from-cyan-50 to-white border-cyan-100">
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500">When</p>
                      <p className="font-semibold">
                        {classData.day_of_week && <span>{classData.day_of_week}</span>}
                        {classData.start_time && classData.end_time && (
                          <span> {classData.start_time} - {classData.end_time}</span>
                        )}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Show term dates if available */}
              {classData.term_dates && (
                <Card className="bg-gradient-to-br from-violet-50 to-white border-violet-100">
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500">Dates</p>
                      <p className="font-semibold">{classData.term_dates}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-100">
                <CardContent className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Location</p>
                    <p className="font-semibold">{classData.address || classData.room_name || 'TBA'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Class Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                {schedules.length === 0 ? (
                  <p className="text-neutral-500">No schedules available for this class.</p>
                ) : (
                  <div className="space-y-3">
                    {schedules.map(schedule => {
                      const isFull = isScheduleFull(schedule);
                      const waitlistEntry = getUserWaitlistForSchedule(schedule.id);
                      
                      return (
                        <div 
                          key={schedule.id}
                          className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                            isFull ? 'bg-amber-50 border border-amber-200' : 'bg-neutral-50 hover:bg-neutral-100'
                          }`}
                        >
                          <div>
                            <p className="font-semibold">{DAYS_OF_WEEK[schedule.day_of_week]}</p>
                            <p className="text-sm text-neutral-500">
                              {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                            </p>
                            {schedule.capacity && (
                              <p className="text-xs text-neutral-400 mt-1">
                                {schedule.current_enrolled || 0}/{schedule.capacity} enrolled
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Add to Calendar Button */}
                            <AddToCalendarButton 
                              classData={classData} 
                              schedule={schedule}
                              variant="ghost"
                              size="sm"
                              className="text-neutral-600 hover:text-neutral-800"
                            />
                            <Badge variant="secondary">{schedule.room_name}</Badge>
                            {isFull && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                                Full
                              </Badge>
                            )}
                            {waitlistEntry && (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                                <Bell className="w-3 h-3 mr-1" />
                                Waitlist #{waitlistEntry.position}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Waitlist Info Card - Show if user is on any waitlist */}
            {userWaitlistEntries.length > 0 && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-blue-800 flex items-center gap-2 text-lg">
                    <Bell className="w-5 h-5" />
                    Your Waitlist Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {userWaitlistEntries.map(entry => {
                      const schedule = schedules.find(s => s.id === entry.schedule_id);
                      return (
                        <div key={entry.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                          <div>
                            <p className="font-medium text-neutral-800">
                              {schedule ? `${DAYS_OF_WEEK[schedule.day_of_week]} ${formatTime(schedule.start_time)}` : 'Schedule'}
                            </p>
                            <p className="text-sm text-neutral-500">
                              Student: {entry.student_name} • Position #{entry.position}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLeaveWaitlist(entry.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Leave
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-sm text-blue-600 mt-3">
                    You'll receive an email when a spot becomes available.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 shadow-xl border-0 bg-gradient-to-br from-white to-purple-50">
              <CardHeader className="pb-4">
                <CardTitle className="font-heading">Book This Class</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Pricing */}
                <div className="space-y-3">
                  {classData.name?.toLowerCase().includes('primatotterina') ? (
                    /* Primatotterina - Monthly subscription pricing */
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-pink-100 to-purple-100 rounded-lg border border-pink-200">
                      <div>
                        <p className="font-medium">Monthly Subscription</p>
                        <p className="text-sm text-neutral-500">Rolling monthly from sign-up date</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-2xl font-bold text-primary">
                          £18.00
                        </p>
                        <p className="text-xs text-neutral-500">per month</p>
                      </div>
                    </div>
                  ) : (
                    /* Flexible pricing display */
                    <>
                      {/* 6-Week Price */}
                      {classData.six_week_price && (
                        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20">
                          <div>
                            <p className="font-medium">6 Weeks - Half Term</p>
                            <p className="text-sm text-neutral-500">Pay for half term</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-xl font-bold text-primary">
                              £{classData.six_week_price.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      )}
                      {/* Full Term Price (12 weeks) */}
                      {classData.term_price && (
                        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg border border-purple-200">
                          <div>
                            <p className="font-medium">12 Weeks - Full Term</p>
                            <p className="text-sm text-neutral-500">Pay for full term</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-xl font-bold text-primary">
                              £{classData.term_price.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      )}
                      {classData.subscription_price && (
                        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-violet-100 to-purple-100 rounded-lg border border-violet-200">
                          <div>
                            <p className="font-medium">Monthly Subscription</p>
                            <p className="text-sm text-neutral-500">Rolling monthly</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-xl font-bold text-violet-600">
                              £{classData.subscription_price.toFixed(2)}
                            </p>
                            <p className="text-xs text-neutral-500">per month</p>
                          </div>
                        </div>
                      )}
                      {classData.one_off_price && (
                        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-100 to-teal-100 rounded-lg border border-cyan-200">
                          <div>
                            <p className="font-medium">Single Class</p>
                            <p className="text-sm text-neutral-500">One-off drop-in</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-xl font-bold text-cyan-600">
                              £{classData.one_off_price.toFixed(2)}
                            </p>
                            <p className="text-xs text-neutral-500">per class</p>
                          </div>
                        </div>
                      )}
                      {!classData.six_week_price && !classData.term_price && !classData.subscription_price && !classData.one_off_price && (
                        <div className="p-4 bg-neutral-100 rounded-lg text-center">
                          <p className="text-neutral-600">Contact us for pricing</p>
                        </div>
                      )}
                    </>
                  )}
                  {classData.bundle_price && classData.bundle_price < classData.term_price && (
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-4 h-4 text-yellow-600" />
                        <p className="font-medium text-yellow-800">Bundle Discount!</p>
                      </div>
                      <p className="text-sm text-yellow-700">
                        Only £{classData.bundle_price.toFixed(2)} when combined with the UPA 3 Hour Course
                      </p>
                    </div>
                  )}
                  {classData.name === 'UPA 3 Hour Course' && (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-purple-700">
                        <strong>Save on extras!</strong> Take both Street Dance and Tap/Ballet alongside this course and pay just £45 each (normally £54).
                      </p>
                    </div>
                  )}
                </div>

                {/* Book Button */}
                {classData.name?.toLowerCase().includes('primatotterina') ? (
                  /* Primatotterina - Direct to subscription enrollment */
                  <Button 
                    className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity text-lg font-semibold"
                    onClick={() => navigate(`/enroll?classId=${classId}&subscription=true`)}
                    data-testid="book-subscription-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Subscribe Now - £18/month
                  </Button>
                ) : (
                  /* Standard classes - Only show Free Trial button */
                  <div className="space-y-3">
                    {/* Primary Free Trial Button */}
                    {classData.offers_free_trial !== false ? (
                      <Button 
                        className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity text-lg font-semibold"
                        onClick={() => navigate(`/free-trial?classId=${classId}`)}
                        data-testid="book-trial-btn"
                      >
                        <Sparkles className="w-5 h-5 mr-2" />
                        Book Your Free Trial
                      </Button>
                    ) : (
                      <Button 
                        className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity text-lg font-semibold"
                        onClick={() => navigate('/contact')}
                        data-testid="contact-btn"
                      >
                        <Sparkles className="w-5 h-5 mr-2" />
                        Contact Us to Enrol
                      </Button>
                    )}
                    
                    <p className="text-center text-neutral-500 text-sm">
                      Try a class for free, then decide if you'd like to join!
                    </p>
                  </div>
                )}

                {/* Join Waitlist Button - Show when any schedule is full */}
                {schedules.some(s => isScheduleFull(s)) && (
                  <Dialog open={waitlistModalOpen} onOpenChange={setWaitlistModalOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline"
                        className="w-full h-12 rounded-full border-2 border-amber-400 text-amber-700 hover:bg-amber-50 font-semibold"
                        data-testid="join-waitlist-btn"
                      >
                        <Bell className="w-5 h-5 mr-2" />
                        Join Waitlist
                      </Button>
                    </DialogTrigger>
                    
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                          <Bell className="w-5 h-5 text-amber-500" />
                          Join Waitlist
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-4 py-4">
                        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                              <p className="font-medium text-amber-800">Class is full</p>
                              <p className="text-sm text-amber-700">
                                Join the waitlist and we'll notify you by email when a spot opens up.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Schedule Selection - Only full schedules */}
                        <div className="space-y-2">
                          <Label>Select Schedule *</Label>
                          <RadioGroup value={selectedSchedule} onValueChange={setSelectedSchedule}>
                            {schedules.filter(s => isScheduleFull(s)).map(schedule => {
                              const alreadyOnWaitlist = getUserWaitlistForSchedule(schedule.id);
                              return (
                                <div key={schedule.id} className="flex items-center space-x-2">
                                  <RadioGroupItem 
                                    value={schedule.id} 
                                    id={`wl-${schedule.id}`} 
                                    disabled={!!alreadyOnWaitlist}
                                  />
                                  <Label htmlFor={`wl-${schedule.id}`} className="cursor-pointer">
                                    {DAYS_OF_WEEK[schedule.day_of_week]} {formatTime(schedule.start_time)}
                                    {alreadyOnWaitlist && (
                                      <span className="ml-2 text-green-600 text-sm flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" /> Already on waitlist
                                      </span>
                                    )}
                                  </Label>
                                </div>
                              );
                            })}
                          </RadioGroup>
                        </div>

                        {/* Student Name */}
                        <div className="space-y-2">
                          <Label htmlFor="waitlist_student_name">Student Name *</Label>
                          <Input
                            id="waitlist_student_name"
                            placeholder="Enter student's name"
                            value={waitlistStudentName}
                            onChange={(e) => setWaitlistStudentName(e.target.value)}
                            data-testid="waitlist-student-name"
                          />
                        </div>
                      </div>

                      <DialogFooter>
                        <Button variant="outline" onClick={() => setWaitlistModalOpen(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleJoinWaitlist}
                          disabled={joiningWaitlist || !selectedSchedule || !waitlistStudentName}
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          data-testid="confirm-waitlist-btn"
                        >
                          {joiningWaitlist ? 'Joining...' : 'Join Waitlist'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}

                {!isAuthenticated && (
                  <p className="text-sm text-center text-neutral-500">
                    <button 
                      onClick={() => navigate('/login')}
                      className="text-primary hover:underline"
                    >
                      Sign in
                    </button>
                    {' '}to book this class
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassDetail;
